"use client";

import { formatDistanceToNow } from "date-fns";
import Image from "next/image";
import Link from "next/link";

interface TierListCardProps {
  tierList: {
    _id: string;
    _creationTime: number;
    title: string;
    slug: string;
    description?: string;
    views: number;
    user: {
      _id: string;
      name: string;
      pictureUrl?: string;
      username?: string;
    };
    tiers: Array<{
      name: string;
      color?: string;
      weapons: Array<{
        _id: string;
        name: string;
        category: string;
        logoUrl?: string | null;
        imageUrl?: string | null;
      }>;
    }>;
    stats: {
      upvotes: number;
      downvotes: number;
      comments: number;
      avgRating: number;
      totalRatings: number;
      wilsonScore?: number;
    };
  };
  featured?: boolean;
}

export function TierListCard({
  tierList,
  featured = false,
}: TierListCardProps) {
  const totalWeapons = tierList.tiers.reduce(
    (sum, tier) => sum + tier.weapons.length,
    0
  );

  return (
    <div className="group flex flex-col overflow-hidden rounded-[8px] border border-[#262A2D] bg-[#141618] transition-colors hover:border-[#F5A23E]/50">
      <div className="h-[3px] bg-gradient-to-r from-[#F5A23E] to-transparent" />

      <div className="flex flex-col gap-4 p-4">
        {/* Header */}
        <div>
          <div className="flex items-start justify-between gap-2">
            <Link
              className="min-w-0 flex-1"
              href={`/tier-lists/${tierList.slug}`}
            >
              <h3 className="truncate font-bold font-heading text-[#EAEBEC] text-base transition-colors group-hover:text-[#F5A23E] sm:text-lg">
                {tierList.title}
              </h3>
            </Link>
            {featured ? (
              <span className="shrink-0 rounded-[3px] bg-[#46C8E0] px-1.5 py-0.5 font-bold font-mono text-[#0a0b0c] text-[10px]">
                FEATURED
              </span>
            ) : null}
          </div>
          {tierList.description ? (
            <p className="mt-1 line-clamp-2 text-[#8B9297] text-xs sm:text-sm">
              {tierList.description}
            </p>
          ) : null}

          {/* Creator + date */}
          <div className="mt-2 flex items-center gap-2 font-mono text-[#6a7075] text-[11px]">
            <Link
              className="flex items-center gap-1.5 transition-colors hover:text-[#EAEBEC]"
              href={`/profile/${tierList.user.username || tierList.user._id}`}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2b6cb0] text-[9px] text-white">
                {tierList.user.name.charAt(0).toUpperCase()}
              </span>
              <span>{tierList.user.name}</span>
            </Link>
            <span>·</span>
            <span>
              {formatDistanceToNow(tierList._creationTime, { addSuffix: true })}
            </span>
          </div>
        </div>

        {/* Tier preview */}
        <div className="flex flex-col gap-2">
          {tierList.tiers.slice(0, 3).map((tier, index) => (
            <div className="flex items-center gap-3" key={tier.name}>
              <div
                className="flex h-7 w-9 shrink-0 items-center justify-center rounded-[4px] font-bold font-mono text-white text-xs"
                style={{
                  backgroundColor: tier.color || `hsl(${index * 60}, 70%, 50%)`,
                }}
              >
                {tier.name.charAt(0)}
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                {tier.weapons.slice(0, 4).map((weapon) => (
                  <div
                    className="flex h-7 w-7 items-center justify-center rounded-[4px] border border-[#262A2D] bg-[#0C0D0E] p-1"
                    key={weapon._id}
                    title={weapon.name}
                  >
                    {weapon.logoUrl ? (
                      <Image
                        alt={weapon.name}
                        className="h-full w-full object-contain"
                        height={20}
                        src={weapon.logoUrl}
                        width={20}
                      />
                    ) : (
                      <span className="font-mono text-[#A9AFB3] text-[10px]">
                        {weapon.name.charAt(0)}
                      </span>
                    )}
                  </div>
                ))}
                {tier.weapons.length > 4 ? (
                  <span className="font-mono text-[#6a7075] text-[10px]">
                    +{tier.weapons.length - 4}
                  </span>
                ) : null}
                <span className="ml-auto font-mono text-[#6a7075] text-[10px]">
                  {tier.weapons.length}
                </span>
              </div>
            </div>
          ))}

          {tierList.tiers.length > 3 ? (
            <div className="rounded-[4px] border border-[#262A2D] border-dashed py-1.5 text-center font-mono text-[#6a7075] text-[10px]">
              +{tierList.tiers.length - 3} more tier
              {tierList.tiers.length - 3 !== 1 ? "s" : ""}
            </div>
          ) : null}
        </div>

        {/* Stats + footer */}
        <div className="flex items-center justify-between border-[#262A2D] border-t pt-3 font-mono text-xs">
          <div className="flex items-center gap-3 text-[#8B9297]">
            <span className="text-[#2ec27e]">▲ {tierList.stats.upvotes}</span>
            {tierList.stats.downvotes > 0 ? (
              <span className="text-[#e5484d]">
                ▼ {tierList.stats.downvotes}
              </span>
            ) : null}
            <span>◎ {tierList.stats.comments}</span>
          </div>
          <Link
            className="text-[#46C8E0] text-[11px] tracking-wider transition-colors hover:text-[#7bd9ea]"
            href={`/tier-lists/${tierList.slug}`}
          >
            VIEW · {totalWeapons} WEAPONS →
          </Link>
        </div>
      </div>
    </div>
  );
}
